import React, { useState } from 'react';
import {
  Package, Search, Filter, Download, Upload, RefreshCw, Eye, Edit2,
  CheckCircle, Clock, Truck, MapPin, Users, X, Save, AlertCircle,
  FileSpreadsheet, Loader2
} from 'lucide-react';

// Mock packages data
const mockPackages = [
  {
    id: 'pkg-001',
    tracking_number: 'TRK-2025-45821',
    bolt_number: 'BOLT-FS-4521',
    status: 'received_jamaica',
    weight_lb: 12.5,
    matched_customer_id: 'cust-001',
    matched_customer_name: 'Marcus Johnson',
    matched_customer_phone: '+1 (876) 555-0123',
    manifest_code: 'MAN-2025-12-15',
    operational_manifest_id: 'bom-001',
    invoice_id: null,
    scanned_at: '2025-12-15T10:30:00',
    created_at: '2025-12-12T09:00:00',
    source: 'KCD Sync'
  },
  {
    id: 'pkg-002',
    tracking_number: 'TRK-2025-45820',
    bolt_number: 'BOLT-FS-4520',
    status: 'ready',
    weight_lb: 8.2,
    matched_customer_id: 'cust-002',
    matched_customer_name: 'Sarah Williams',
    matched_customer_phone: '+1 (876) 555-0456',
    manifest_code: 'MAN-2025-12-15',
    operational_manifest_id: 'bom-001',
    invoice_id: 'inv-001',
    scanned_at: '2025-12-15T10:32:00',
    created_at: '2025-12-12T09:05:00',
    source: 'KCD Sync'
  },
  {
    id: 'pkg-003',
    tracking_number: 'TRK-2025-45819',
    bolt_number: 'BOLT-FS-4519',
    status: 'in_transit',
    weight_lb: 22.1,
    matched_customer_id: 'cust-003',
    matched_customer_name: 'James Brown',
    matched_customer_phone: '+1 (876) 555-0789',
    manifest_code: 'MAN-2025-12-14',
    operational_manifest_id: null,
    invoice_id: null,
    scanned_at: null,
    created_at: '2025-12-11T14:00:00',
    source: 'KCD Sync'
  },
  {
    id: 'pkg-004',
    tracking_number: 'TRK-2025-45818',
    bolt_number: 'BOLT-FS-4518',
    status: 'pending',
    weight_lb: 5.8,
    matched_customer_id: null,
    matched_customer_name: 'Emily Davis',
    matched_customer_phone: null,
    manifest_code: 'MAN-2025-12-14',
    operational_manifest_id: null,
    invoice_id: null,
    scanned_at: null,
    created_at: '2025-12-10T11:00:00',
    source: 'Excel Import'
  },
  {
    id: 'pkg-005',
    tracking_number: 'TRK-2025-45817',
    bolt_number: 'BOLT-FS-4517',
    status: 'delivered',
    weight_lb: 15.3,
    matched_customer_id: 'cust-005',
    matched_customer_name: 'Michael Chen',
    matched_customer_phone: '+1 (876) 555-0321',
    manifest_code: 'MAN-2025-12-10',
    operational_manifest_id: 'bom-002',
    invoice_id: 'inv-002',
    scanned_at: '2025-12-10T09:15:00',
    created_at: '2025-12-08T10:00:00',
    source: 'KCD Sync'
  },
];

const statusConfig = {
  pending: { label: 'Pending', color: 'gray', icon: Clock },
  in_transit: { label: 'In Transit', color: 'blue', icon: Truck },
  received_jamaica: { label: 'Received JA', color: 'cyan', icon: MapPin },
  ready: { label: 'Ready', color: 'lime', icon: CheckCircle },
  delivered: { label: 'Delivered', color: 'purple', icon: CheckCircle },
};

const statusFilters = [
  { value: 'all', label: 'All Packages' },
  { value: 'pending', label: 'Pending' },
  { value: 'in_transit', label: 'In Transit' },
  { value: 'received_jamaica', label: 'Received JA' },
  { value: 'ready', label: 'Ready' },
  { value: 'delivered', label: 'Delivered' },
];

export default function TenantPackages() {
  const [packages, setPackages] = useState(mockPackages);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPackages, setSelectedPackages] = useState([]);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const filteredPackages = packages.filter(pkg => {
    const matchesFilter = filter === 'all' || pkg.status === filter;
    const matchesSearch = 
      pkg.tracking_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pkg.bolt_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (pkg.matched_customer_name && pkg.matched_customer_name.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesFilter && matchesSearch;
  });

  const handleSync = () => {
    setSyncing(true);
    setTimeout(() => {
      setSyncing(false);
      alert('Synced 12 new packages from KCD warehouse system');
    }, 2000);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedPackages(filteredPackages.map(p => p.id));
    } else {
      setSelectedPackages([]);
    }
  };

  const handleSelectPackage = (id) => {
    if (selectedPackages.includes(id)) {
      setSelectedPackages(selectedPackages.filter(p => p !== id));
    } else {
      setSelectedPackages([...selectedPackages, id]);
    }
  };

  const openDetail = (pkg) => {
    setSelectedPackage(pkg);
    setShowDetailModal(true);
  };

  // Stats
  const stats = {
    total: packages.length,
    pending: packages.filter(p => p.status === 'pending').length,
    inTransit: packages.filter(p => p.status === 'in_transit').length,
    received: packages.filter(p => p.status === 'received_jamaica').length,
    ready: packages.filter(p => p.status === 'ready').length,
    unmatched: packages.filter(p => !p.matched_customer_id).length,
  };

  return (
    <div className="tenant-packages-page" data-testid="tenant-packages">
      {/* Stats Row */}
      <div className="packages-stats-row">
        <div className="stat-chip">{stats.total} Total</div>
        <div className="stat-chip gray">{stats.pending} Pending</div>
        <div className="stat-chip blue">{stats.inTransit} In Transit</div>
        <div className="stat-chip cyan">{stats.received} Received JA</div>
        <div className="stat-chip lime">{stats.ready} Ready</div>
        {stats.unmatched > 0 && (
          <div className="stat-chip amber">{stats.unmatched} Unmatched</div>
        )}
      </div>

      {/* Toolbar */}
      <div className="packages-toolbar">
        <div className="toolbar-left">
          <div className="search-box">
            <Search size={18} />
            <input
              type="text"
              placeholder="Search tracking, BOLT #, customer..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="package-search"
            />
          </div>
          
          <div className="filter-dropdown">
            <Filter size={16} />
            <select 
              value={filter} 
              onChange={(e) => setFilter(e.target.value)}
              data-testid="status-filter"
            >
              {statusFilters.map((f) => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="toolbar-right">
          <button 
            className="btn btn-secondary"
            onClick={handleSync}
            disabled={syncing}
            data-testid="sync-btn"
          >
            {syncing ? <Loader2 size={14} className="spinning" /> : <RefreshCw size={14} />}
            {syncing ? 'Syncing...' : 'Sync from KCD'}
          </button>
          
          <button 
            className="btn btn-secondary"
            onClick={() => setShowImportModal(true)}
            data-testid="import-btn"
          >
            <FileSpreadsheet size={14} /> Import Excel
          </button>
          
          {selectedPackages.length > 0 && (
            <button 
              className="btn btn-primary"
              onClick={() => setShowBulkModal(true)}
              data-testid="bulk-update-btn"
            >
              <Edit2 size={14} /> Bulk Update ({selectedPackages.length})
            </button>
          )}
          
          <button className="btn btn-ghost" data-testid="export-btn">
            <Download size={14} /> Export
          </button>
        </div>
      </div>

      {/* Packages Table */}
      <div className="packages-table-card">
        <div className="table-header">
          <span className="checkbox-cell">
            <input 
              type="checkbox"
              onChange={handleSelectAll}
              checked={selectedPackages.length === filteredPackages.length && filteredPackages.length > 0}
              data-testid="select-all"
            />
          </span>
          <span>Tracking / BOLT #</span>
          <span>Customer</span>
          <span>Status</span>
          <span>Weight</span>
          <span>Manifest</span>
          <span>Source</span>
          <span>Actions</span>
        </div>
        
        {filteredPackages.map((pkg) => {
          const StatusIcon = statusConfig[pkg.status]?.icon || Clock;
          return (
            <div 
              key={pkg.id} 
              className={`table-row ${selectedPackages.includes(pkg.id) ? 'selected' : ''}`}
              data-testid={`row-${pkg.id}`}
            >
              <span className="checkbox-cell">
                <input 
                  type="checkbox"
                  checked={selectedPackages.includes(pkg.id)}
                  onChange={() => handleSelectPackage(pkg.id)}
                />
              </span>
              
              <div className="tracking-cell">
                <span className="tracking-number">{pkg.tracking_number}</span>
                <span className="bolt-number">{pkg.bolt_number}</span>
              </div>
              
              <div className="customer-cell">
                {pkg.matched_customer_id ? (
                  <>
                    <span className="customer-name">{pkg.matched_customer_name}</span>
                    <span className="customer-status matched">
                      <CheckCircle size={10} /> Matched
                    </span>
                  </>
                ) : (
                  <>
                    <span className="customer-name dim">{pkg.matched_customer_name || 'Unknown'}</span>
                    <span className="customer-status unmatched">
                      <AlertCircle size={10} /> Unmatched
                    </span>
                  </>
                )}
              </div>
              
              <span className={`status-badge ${statusConfig[pkg.status]?.color}`}>
                <StatusIcon size={12} />
                {statusConfig[pkg.status]?.label}
              </span>
              
              <span className="weight-cell">{pkg.weight_lb} lbs</span>
              
              <span className="manifest-cell">{pkg.manifest_code || '-'}</span>
              
              <span className="source-cell">{pkg.source}</span>
              
              <div className="actions-cell">
                <button 
                  className="action-btn"
                  onClick={() => openDetail(pkg)}
                  data-testid={`view-${pkg.id}`}
                >
                  <Eye size={14} />
                </button>
                <button 
                  className="action-btn"
                  data-testid={`edit-${pkg.id}`}
                >
                  <Edit2 size={14} />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Package Detail Modal */}
      {showDetailModal && selectedPackage && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="modal-content package-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h3>Package Details</h3>
                <span className="modal-subtitle">{selectedPackage.bolt_number}</span>
              </div>
              <button className="close-btn" onClick={() => setShowDetailModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              <div className={`status-hero ${statusConfig[selectedPackage.status]?.color}`}>
                {React.createElement(statusConfig[selectedPackage.status]?.icon || Clock, { size: 18 })}
                <span>{statusConfig[selectedPackage.status]?.label}</span>
              </div>

              <div className="detail-section">
                <h4>Tracking Information</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="label">Tracking Number</span>
                    <span className="value mono">{selectedPackage.tracking_number}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">BOLT Number</span>
                    <span className="value mono">{selectedPackage.bolt_number}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Weight</span>
                    <span className="value">{selectedPackage.weight_lb} lbs</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Manifest Code</span>
                    <span className="value mono">{selectedPackage.manifest_code || '-'}</span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h4>Customer Information</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="label">Customer Name</span>
                    <span className="value">{selectedPackage.matched_customer_name || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Phone</span>
                    <span className="value">{selectedPackage.matched_customer_phone || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Match Status</span>
                    <span className={`value ${selectedPackage.matched_customer_id ? 'matched' : 'unmatched'}`}>
                      {selectedPackage.matched_customer_id ? 'Matched' : 'Unmatched'}
                    </span>
                  </div>
                </div>
                {!selectedPackage.matched_customer_id && (
                  <button className="btn btn-secondary btn-sm match-btn">
                    <Users size={14} /> Match to Customer
                  </button>
                )}
              </div>

              <div className="detail-section">
                <h4>Processing Information</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="label">Source</span>
                    <span className="value">{selectedPackage.source}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Created</span>
                    <span className="value">{new Date(selectedPackage.created_at).toLocaleString()}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Scanned</span>
                    <span className="value">
                      {selectedPackage.scanned_at 
                        ? new Date(selectedPackage.scanned_at).toLocaleString() 
                        : 'Not scanned'}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Invoice</span>
                    <span className="value">{selectedPackage.invoice_id || 'Not invoiced'}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowDetailModal(false)}>
                Close
              </button>
              <button className="btn btn-primary" data-testid="update-status-btn">
                Update Status
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Update Modal */}
      {showBulkModal && (
        <div className="modal-overlay" onClick={() => setShowBulkModal(false)}>
          <div className="modal-content bulk-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Bulk Status Update</h3>
              <button className="close-btn" onClick={() => setShowBulkModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              <p className="bulk-count">{selectedPackages.length} packages selected</p>
              
              <div className="form-group">
                <label>New Status</label>
                <select data-testid="bulk-status-select">
                  <option value="">Select status...</option>
                  {Object.entries(statusConfig).map(([key, config]) => (
                    <option key={key} value={key}>{config.label}</option>
                  ))}
                </select>
              </div>

              <div className="selected-packages">
                <h4>Selected Packages</h4>
                <div className="package-chips">
                  {selectedPackages.slice(0, 5).map(id => {
                    const pkg = packages.find(p => p.id === id);
                    return (
                      <span key={id} className="package-chip">
                        {pkg?.bolt_number}
                      </span>
                    );
                  })}
                  {selectedPackages.length > 5 && (
                    <span className="package-chip more">+{selectedPackages.length - 5} more</span>
                  )}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowBulkModal(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" data-testid="apply-bulk-update">
                Apply Update
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="modal-overlay" onClick={() => setShowImportModal(false)}>
          <div className="modal-content import-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Import from Excel</h3>
              <button className="close-btn" onClick={() => setShowImportModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              <div className="upload-zone">
                <Upload size={48} />
                <h4>Drop Excel/CSV file here</h4>
                <p>or click to browse</p>
                <span className="formats">Supports .xlsx, .xls, .csv</span>
              </div>

              <div className="import-info">
                <h4>Required Columns</h4>
                <ul>
                  <li><code>tracking_number</code> - Package tracking number</li>
                  <li><code>manifest_code</code> - Manifest/shipment code</li>
                  <li><code>weight_lb</code> - Weight in pounds (optional)</li>
                  <li><code>customer_name</code> - Customer name (optional)</li>
                </ul>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowImportModal(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" data-testid="start-import">
                Import
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
